import {assertAndContinue} from 'Asserts';
import {LABELING_STRATEGIES_REQUIRING_NAME} from 'constants/RegistryConstants';
import {
  CONCENTRATION_COLUMN_TYPES,
  DESTINATION_COLUMN_TYPES,
  SOURCE_COLUMN_TYPES,
  VOLUME_COLUMN_TYPES,
} from 'lab-automation/settings/automation-output-file-config/AutomationOutputFileColumnUtils';
import {assertNever} from 'util/Types';

import {
  AutomationOutputFileColumnType,
  AutomationOutputFileColumnTypeMetadata,
  AutomationOutputFileEntitySchemaInfo,
  getColumnTypeDisplayName,
  isTransfer,
  OutputFileAction,
  SchemaInfoFields,
  SetColumnsMappingType,
  TagSchemaInfoActionType,
  TransformationFieldType,
} from '../RunSchemaSpecConstants';

export enum SetColumnsMapErrorType {
  ACTION_REQUIRES_NEW_ENTITY_NAME,
  CALCULATE_FINAL_CONCENTRATION_NO_CONCENTRATION_COLUMN,
  CONCENTRATION_UNITS_NOT_SET,
  LABELING_STRATEGY_REQUIRES_NEW_ENTITY_NAME,
  MISSING_REQUIRED_PAIR,
  NO_COLUMN_NAME,
  NO_COLUMN_TYPE,
  PURE_TRANSFER_NO_INPUT_OR_VOLUME_COLUMNS,
  PURE_TRANSFER_NO_OUTPUT_COLUMNS,
  SOURCE_CONTENTS_NO_SOURCE_COLUMN,
  SOURCE_WITHOUT_DESTINATION,
  UNIQUE_COLUMN_TYPE,
  VOLUME_UNITS_NOT_SET,
}

export interface SetColumnsMapError {
  type: SetColumnsMapErrorType;
  message: string;
}

const PAIRED_TYPES: Array<Set<AutomationOutputFileColumnType>> = [
  new Set([
    AutomationOutputFileColumnType.INPUT_BOX_BARCODE,
    AutomationOutputFileColumnType.INPUT_BOX_COORDINATES,
  ]),
  new Set([
    AutomationOutputFileColumnType.INPUT_PLATE_BARCODE,
    AutomationOutputFileColumnType.INPUT_PLATE_COORDINATES,
  ]),
  new Set([
    AutomationOutputFileColumnType.OUTPUT_PLATE_BARCODE,
    AutomationOutputFileColumnType.OUTPUT_PLATE_COORDINATES,
  ]),
  new Set([
    AutomationOutputFileColumnType.OUTPUT_BOX_BARCODE,
    AutomationOutputFileColumnType.OUTPUT_BOX_COORDINATES,
  ]),
];

// Keep in sync with benchling/lab_automation/spec/automation_output_file_config_spec_editor.py
const COLUMN_TYPES_THAT_CAN_REPEAT = new Set<AutomationOutputFileColumnType>([
  AutomationOutputFileColumnType.ALIAS,
  AutomationOutputFileColumnType.DATASET,
  AutomationOutputFileColumnType.IGNORE,
  AutomationOutputFileColumnType.SCHEMA_FIELD,
]);

function getErrorMessage(
  errorType: SetColumnsMapErrorType,
  errorIndices: Array<number>,
  includedType?: AutomationOutputFileColumnType,
  excludedType?: AutomationOutputFileColumnType
): string {
  const multipleIndices: boolean = errorIndices.length > 1;
  switch (errorType) {
    case SetColumnsMapErrorType.ACTION_REQUIRES_NEW_ENTITY_NAME:
      return `New Entity Name column required when updating existing entities.`;
    case SetColumnsMapErrorType.CALCULATE_FINAL_CONCENTRATION_NO_CONCENTRATION_COLUMN:
      return `Concentration column required when using Calculate Final Concentration.`;
    case SetColumnsMapErrorType.CONCENTRATION_UNITS_NOT_SET:
      return `Concentration units required when ${getColumnTypeDisplayName(includedType!)} is present.`;
    case SetColumnsMapErrorType.LABELING_STRATEGY_REQUIRES_NEW_ENTITY_NAME:
      return `New Entity Name column required for naming option.`;
    case SetColumnsMapErrorType.MISSING_REQUIRED_PAIR:
      return `${getColumnTypeDisplayName(excludedType!)} required when ${getColumnTypeDisplayName(
        includedType!
      )} is present.`;
    case SetColumnsMapErrorType.NO_COLUMN_NAME:
      return multipleIndices
        ? `Column indices ${errorIndices} are missing column names.`
        : `Column index ${errorIndices} is missing a column name.`;
    case SetColumnsMapErrorType.NO_COLUMN_TYPE:
      return multipleIndices
        ? `Column indices ${errorIndices} are missing column types.`
        : `Column index ${errorIndices} is missing a column type.`;
    case SetColumnsMapErrorType.PURE_TRANSFER_NO_INPUT_OR_VOLUME_COLUMNS:
      return `Source or volume columns required when performing a Pure Transfer.`;
    case SetColumnsMapErrorType.PURE_TRANSFER_NO_OUTPUT_COLUMNS:
      return `Destination columns required when performing a Pure Transfer.`;
    case SetColumnsMapErrorType.SOURCE_CONTENTS_NO_SOURCE_COLUMN:
      return `Source column is required when using Source Contents.`;
    case SetColumnsMapErrorType.SOURCE_WITHOUT_DESTINATION:
      return 'Transfer destination must be set if transfer source is set.';
    case SetColumnsMapErrorType.UNIQUE_COLUMN_TYPE:
      return `${getColumnTypeDisplayName(includedType!)} must only be set for one column.`;
    case SetColumnsMapErrorType.VOLUME_UNITS_NOT_SET:
      return `Volume units required when ${getColumnTypeDisplayName(includedType!)} is present.`;
    default:
      assertAndContinue(false, `Invalid errorType: ${assertNever(errorType)}`);
      return 'An unknown error occurred';
  }
}

function _getSetColumnMapError(
  errorType: SetColumnsMapErrorType,
  errorIndices: Array<number>,
  includedType?: AutomationOutputFileColumnType,
  excludedType?: AutomationOutputFileColumnType
): SetColumnsMapError {
  return {type: errorType, message: getErrorMessage(errorType, errorIndices, includedType, excludedType)};
}

function _verifyPairs(
  columnTypes: Set<AutomationOutputFileColumnType>,
  errors: Array<SetColumnsMapError>
): void {
  const errorType: SetColumnsMapErrorType = SetColumnsMapErrorType.MISSING_REQUIRED_PAIR;
  PAIRED_TYPES.forEach((pairedTypesSet) => {
    const intersection = [...columnTypes].filter((i) => pairedTypesSet.has(i));
    if (intersection.length === 1) {
      const nonIntersection = [...pairedTypesSet].filter((i) => !intersection.includes(i));
      errors.push(_getSetColumnMapError(errorType, [], intersection[0], nonIntersection[0]));
    }
  });
  if (
    columnTypes.has(AutomationOutputFileColumnType.FINAL_QUANTITY_AMOUNT) &&
    !columnTypes.has(AutomationOutputFileColumnType.TRANSFER_QUANTITY_AMOUNT)
  ) {
    errors.push(
      _getSetColumnMapError(
        errorType,
        [],
        AutomationOutputFileColumnType.FINAL_QUANTITY_AMOUNT,
        AutomationOutputFileColumnType.TRANSFER_QUANTITY_AMOUNT
      )
    );
  }
  if (
    columnTypes.has(AutomationOutputFileColumnType.SOURCE_FINAL_QUANTITY_AMOUNT) &&
    !columnTypes.has(AutomationOutputFileColumnType.TRANSFER_QUANTITY_AMOUNT)
  ) {
    errors.push(
      _getSetColumnMapError(
        errorType,
        [],
        AutomationOutputFileColumnType.SOURCE_FINAL_QUANTITY_AMOUNT,
        AutomationOutputFileColumnType.TRANSFER_QUANTITY_AMOUNT
      )
    );
  }
}

function _verifyTransfer(
  columnTypes: Set<AutomationOutputFileColumnType>,
  errors: Array<SetColumnsMapError>,
  actionType: OutputFileAction,
  shouldPerformPlateTransfer: boolean
): void {
  if (!isTransfer(actionType, shouldPerformPlateTransfer)) {
    return;
  }

  const destinationIntersection = [...columnTypes].filter((i) => DESTINATION_COLUMN_TYPES.has(i));
  const sourceIntersection = [...columnTypes].filter((i) => SOURCE_COLUMN_TYPES.has(i));
  const hasSource = sourceIntersection.length !== 0;
  const hasDestination = destinationIntersection.length !== 0;

  if (actionType !== OutputFileAction.PURE_TRANSFER) {
    if (hasSource && !hasDestination) {
      errors.push(_getSetColumnMapError(SetColumnsMapErrorType.SOURCE_WITHOUT_DESTINATION, []));
    }
    return;
  }

  const volumeIntersection = [...columnTypes].filter((i) => VOLUME_COLUMN_TYPES.has(i));
  const hasVolume = volumeIntersection.length !== 0;
  if (!hasVolume && !hasSource && !columnTypes.has(AutomationOutputFileColumnType.INPUT_ENTITY_NAME)) {
    errors.push(_getSetColumnMapError(SetColumnsMapErrorType.PURE_TRANSFER_NO_INPUT_OR_VOLUME_COLUMNS, []));
  }
  if (!hasDestination) {
    errors.push(_getSetColumnMapError(SetColumnsMapErrorType.PURE_TRANSFER_NO_OUTPUT_COLUMNS, []));
  }
}

function _verifySchemaInfoFields(
  columnTypes: Set<AutomationOutputFileColumnType>,
  schemaInfoFields: SchemaInfoFields,
  errors: Array<SetColumnsMapError>
): void {
  Object.values(schemaInfoFields).forEach((transformationFieldInfo) => {
    if (transformationFieldInfo.type === TransformationFieldType.CALCULATE_FINAL_CONCENTRATION) {
      const intersection = [...columnTypes].filter((i) => CONCENTRATION_COLUMN_TYPES.has(i));
      if (intersection.length === 0) {
        errors.push(
          _getSetColumnMapError(
            SetColumnsMapErrorType.CALCULATE_FINAL_CONCENTRATION_NO_CONCENTRATION_COLUMN,
            []
          )
        );
      }
    }
    if (transformationFieldInfo.type === TransformationFieldType.SOURCE_CONTENTS) {
      const intersection = [...columnTypes].filter((i) => SOURCE_COLUMN_TYPES.has(i));
      if (intersection.length === 0) {
        errors.push(_getSetColumnMapError(SetColumnsMapErrorType.SOURCE_CONTENTS_NO_SOURCE_COLUMN, []));
      }
    }
  });
}

function _verifyEntitySchemaInfo(
  columnTypes: Set<AutomationOutputFileColumnType>,
  entitySchemaInfo: AutomationOutputFileEntitySchemaInfo | null | undefined,
  errors: Array<SetColumnsMapError>
): void {
  if (entitySchemaInfo == null) {
    return;
  }
  if (!columnTypes.has(AutomationOutputFileColumnType.NEW_ENTITY_NAME)) {
    if (entitySchemaInfo.actionType !== TagSchemaInfoActionType.CREATE) {
      errors.push(_getSetColumnMapError(SetColumnsMapErrorType.ACTION_REQUIRES_NEW_ENTITY_NAME, []));
    }
    if (LABELING_STRATEGIES_REQUIRING_NAME.includes(entitySchemaInfo.labelingStrategy)) {
      errors.push(
        _getSetColumnMapError(SetColumnsMapErrorType.LABELING_STRATEGY_REQUIRES_NEW_ENTITY_NAME, [])
      );
    }
  }
}

export function getSetColumnMapErrors(
  columnTypeMetadatas: Array<AutomationOutputFileColumnTypeMetadata>,
  setColumnsMappingType: SetColumnsMappingType,
  volumeUnits: string,
  concentrationUnits: string,
  actionType: OutputFileAction,
  schemaInfoFields: SchemaInfoFields,
  entitySchemaInfo: AutomationOutputFileEntitySchemaInfo | null | undefined,
  shouldPerformPlateTransfer: boolean
): Array<SetColumnsMapError> {
  const errors: Array<SetColumnsMapError> = [];
  const columnTypes: Set<AutomationOutputFileColumnType> = new Set();
  const columnIndicesWithoutTypes: Array<number> = [];
  const columnIndicesWithoutNames: Array<number> = [];

  // Fetch all the columnTypes, while verifying uniqueness and volumeUnits/concentrationUnits
  columnTypeMetadatas.forEach((columnTypeMetadata, index) => {
    const columnType = columnTypeMetadata.columnType;
    const name = columnTypeMetadata.name;

    if (setColumnsMappingType === SetColumnsMappingType.BY_COLUMN_NAME && !name) {
      columnIndicesWithoutNames.push(index + 1);
    }

    if (!columnType) {
      columnIndicesWithoutTypes.push(index + 1);
    } else if (!COLUMN_TYPES_THAT_CAN_REPEAT.has(columnType)) {
      // Unique Columns
      if (columnTypes.has(columnType)) {
        errors.push(_getSetColumnMapError(SetColumnsMapErrorType.UNIQUE_COLUMN_TYPE, [], columnType));
      }
      columnTypes.add(columnType);

      // Missing volumeUnits
      if (VOLUME_COLUMN_TYPES.has(columnType) && !volumeUnits) {
        errors.push(_getSetColumnMapError(SetColumnsMapErrorType.VOLUME_UNITS_NOT_SET, [], columnType));
      }
      // Missing concentrationUnits
      if (CONCENTRATION_COLUMN_TYPES.has(columnType) && !concentrationUnits) {
        errors.push(
          _getSetColumnMapError(SetColumnsMapErrorType.CONCENTRATION_UNITS_NOT_SET, [], columnType)
        );
      }
    }
  });

  // No Column Name
  if (columnIndicesWithoutNames.length) {
    errors.push(_getSetColumnMapError(SetColumnsMapErrorType.NO_COLUMN_NAME, columnIndicesWithoutNames));
  }
  // No Column Type
  if (columnIndicesWithoutTypes.length) {
    errors.push(_getSetColumnMapError(SetColumnsMapErrorType.NO_COLUMN_TYPE, columnIndicesWithoutTypes));
  }

  _verifyPairs(columnTypes, errors);
  _verifyTransfer(columnTypes, errors, actionType, shouldPerformPlateTransfer);
  _verifySchemaInfoFields(columnTypes, schemaInfoFields, errors);
  _verifyEntitySchemaInfo(columnTypes, entitySchemaInfo, errors);

  return errors;
}
